import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const arcSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const listItemRemove = keyframes`
	from {
		opacity: 1;
		transform: translateX(0) scale(1);
		max-height: 88px;
	}

	to {
		opacity: 0;
		transform: translateX(-14px) scale(0.97);
		max-height: 0;
		padding-top: 0;
		padding-bottom: 0;
		margin-top: 0;
		margin-bottom: 0;
	}
`;

export const ListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	justify-content: flex-start;
`;

export const ListHeader = styled.div`
	display: flex;
	flex-direction: column;
	padding: 8px 4px 6px;
	flex-shrink: 0;

	h2 {
		margin: 0;
		font-family: ${THEME.fontDisplay};
		font-size: clamp(1.85rem, 2.4vw, 2.1rem);
		font-weight: 800;
		line-height: 1;
		color: ${THEME.text};
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 10px 10px 6px;

		h2 {
			font-size: 1.35rem;
		}
	}
`;

export const SearchInputWrapper = styled.div`
	display: flex;
	align-items: center;
	border-radius: 12px;
	padding: 8px 12px;
	background: rgba(0, 0, 0, 0.22);
	border: 1px solid rgba(255, 255, 255, 0.08);
`;

export const StyledSearchIcon = styled.div`
	color: rgba(216, 189, 228, 0.72);
	margin-right: 8px;
	width: 16px;
	height: 16px;

	img {
		width: 100%;
		height: auto;
		opacity: 0.72;
	}
`;

export const SearchInput = styled.input`
	flex-grow: 1;
	background: none;
	border: none;
	color: ${THEME.text};
	font-size: 0.88rem;

	&::placeholder {
		color: rgba(216, 189, 228, 0.62);
	}

	&:focus {
		outline: none;
	}
`;

export const List = styled.ul`
	list-style: none;
	padding: 0 2px 4px;
	margin: 0;
	overflow-y: auto;
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
	gap: 8px;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

export const LoadingText = styled.div`
	flex: 0 0 auto;
	padding: 16px;
	color: rgba(216, 189, 228, 0.72);
	text-align: center;
`;

export const ListItemSkeleton = styled.li`
	width: 100%;
	flex: 0 0 auto;
	border-radius: 12px;
	padding: 8px 4px;
	display: grid;
	grid-template-columns: 50px minmax(0, 1fr);
	gap: 8px;
	align-items: center;
	pointer-events: none;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 8px 6px;
	}
`;

export const ListItemSkeletonInfo = styled.div`
	min-width: 0;
	display: grid;
	gap: 8px;
`;

export const ListItem = styled.li`
	width: 100%;
	flex: 0 0 auto;
	border: 1px solid transparent;
	border-radius: 12px;
	padding: 8px 4px;
	display: grid;
	grid-template-columns: 50px minmax(0, 1fr);
	gap: 8px;
	align-items: center;
	cursor: pointer;
	background: transparent;
	color: ${THEME.text};
	transition:
		background 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease,
		transform 180ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 8px 6px;
	}

	&:hover {
		transform: translateY(-1px);
		border-color: transparent;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
		box-shadow: 0 10px 24px rgba(0, 0, 0, 0.16), 0 0 14px rgba(255, 77, 242, 0.045);
	}

	${(props) =>
		props.isactive &&
		css`
			border-color: rgba(255, 255, 255, 0.08);
			background: rgba(255, 255, 255, 0.055);
			box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

			&:hover {
				transform: none;
				border-color: rgba(255, 255, 255, 0.08);
				background: rgba(255, 255, 255, 0.055);
				box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
			}
		`}

	${(props) =>
		props.$removing &&
		css`
			overflow: hidden;
			pointer-events: none;
			animation: ${listItemRemove} 280ms ease forwards;

			&:hover {
				transform: none;
				box-shadow: none;
				background: transparent;
			}
		`}

	@media (prefers-reduced-motion: reduce) {
		${(props) =>
			props.$removing &&
			css`
				animation-duration: 1ms;
			`}
	}
`;

export const DeleteConversationButton = styled.button`
	border: 0;
	border-radius: 7px;
	width: 26px;
	height: 26px;
	padding: 0;
	display: grid;
	place-items: center;
	flex-shrink: 0;
	color: rgba(255, 245, 255, 0.92);
	background: rgba(255, 255, 255, 0.08);
	cursor: pointer;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.18s ease, background 0.18s ease, color 0.18s ease;

	${ListItem}:hover &,
	${ListItem}:focus-within & {
		opacity: 1;
		pointer-events: auto;
	}

	&:hover:not(:disabled) {
		background: rgba(255, 92, 122, 0.2);
		color: #ffd0da;
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	svg {
		width: 14px;
		height: 14px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		opacity: 1;
		pointer-events: auto;
	}
`;

export const AvatarWrap = styled.div`
	position: relative;
	width: 50px;
	height: 50px;
	flex-shrink: 0;
`;

export const ProfileImage = styled.img`
	width: 50px;
	height: 50px;
	border-radius: 50%;
	object-fit: cover;
	border: 1px solid rgba(255, 255, 255, 0.25);
`;

export const OnlineDot = styled.span`
	position: absolute;
	right: -1px;
	bottom: 3px;
	width: 15px;
	height: 15px;
	border-radius: 50%;
	background: #22d865;
	border: 3px solid #150c1f;
	box-shadow: 0 0 10px rgba(34, 216, 101, 0.7);
`;

export const ArcLoader = styled.div`
	width: 54px;
	height: 54px;
	position: relative;
	flex-shrink: 0;
	animation: ${arcSpin} 1s linear infinite;

	&::after {
		content: '';
		display: block;
		width: 100%;
		height: 100%;
		border: 3px solid transparent;
		border-top-color: ${THEME.pink};
		border-radius: 50%;
	}
`;

export const Info = styled.div`
	min-width: 0;
	display: grid;
	gap: 4px;
`;

export const HeaderRow = styled.div`
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 6px;
`;

export const HeaderActions = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	flex-shrink: 0;
	margin-left: auto;
`;

export const Name = styled.strong`
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 1.02rem;
	font-weight: 700;
	color: ${THEME.text};
`;

export const Timestamp = styled.time`
	flex: 0 0 auto;
	color: rgba(216, 189, 228, 0.78);
	font-size: 0.76rem;
	font-weight: 500;
	white-space: nowrap;
`;

export const LastMessage = styled.small`
	display: block;
	color: rgba(216, 189, 228, 0.78);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 0.9rem;
	line-height: 1.35;
`;

export const StatusSection = styled.div`
	display: none;
`;

export const UnreadBadge = styled.div`
	display: none;
`;
